import { Link } from "react-router-dom";
import { Button } from "@jambushrushti/components/ui/button";
import {
  Database,
  Search,
  FileText,
  Globe,
  ArrowRight,
  Sparkles,
} from "lucide-react";

const researchTools = [
  {
    title: "SodhSanchay",
    subtitle: "Research Metadata",
    description:
      "Store and organize your research notes, metadata, and scholarly references in a centralized and searchable repository.",
    icon: Database,
    href: "/research/sodhsanchay",
    iconBg: "linear-gradient(135deg, #C9A227 0%, #E3C075 100%)",
    accent: "#C9A227",
  },
  {
    title: "SodhSandarbh",
    subtitle: "Search & Export",
    description:
      "Powerful search and export features across research entries with advanced filters and cross-referencing.",
    icon: Search,
    href: "/research/sodhsandarbh",
    iconBg: "linear-gradient(135deg, #C9A227 0%, #E3C075 100%)",
    accent: "#C9A227",
  },
  {
    title: "Śabdasaṅgraha",
    subtitle: "PDF Search",
    description:
      "Search inside Jain PDFs and manuscripts using our AI-powered text extraction and discovery tools.",
    icon: FileText,
    href: "/research/shabdasangraha",
    iconBg: "linear-gradient(135deg, #C9A227 0%, #E3C075 100%)",
    accent: "#C9A227",
  },
  {
    title: "Shastrasandarbha",
    subtitle: "External Sources",
    description:
      "AI-assisted search across Archive.org, HathiTrust, JSTOR, and more to find Jain books worldwide.",
    icon: Globe,
    href: "/research/shastrasandarbha",
    iconBg: "linear-gradient(135deg, #C9A227 0%, #E3C075 100%)",
    accent: "#C9A227",
  },
];

export function ResearchToolsSection() {
  return (
    <section
      className="relative overflow-hidden py-10 sm:py-14 lg:py-28"
      style={{
        background:
          "linear-gradient(180deg, #FAF7F2 0%, #F5EFE6 50%, #FAF7F2 100%)",
      }}
    >
      {/* Decorative background elements */}
      <div
        className="absolute left-10 top-20 h-72 w-72 rounded-full opacity-30"
        style={{
          background:
            "radial-gradient(circle, rgba(201,162,39,0.1) 0%, transparent 70%)",
        }}
      />
      <div
        className="absolute bottom-20 right-10 h-96 w-96 rounded-full opacity-30"
        style={{
          background:
            "radial-gradient(circle, rgba(201,162,39,0.08) 0%, transparent 70%)",
        }}
      />

      <div className="container relative z-10 mx-auto px-6">
        {/* Section Header */}
        <div className="mx-auto mb-14 max-w-2xl text-center">
          <div
            className="mb-5 inline-flex items-center gap-2 rounded-full px-4 py-2"
            style={{
              backgroundColor: "rgba(201,162,39,0.1)",
              border: "1px solid rgba(201,162,39,0.2)",
            }}
          >
            <Sparkles className="h-4 w-4" style={{ color: "#C9A227" }} />
            <span
              className="text-sm font-medium"
              style={{ fontFamily: "Inter, sans-serif", color: "#C9A227" }}
            >
              Jain Knowledge Research Ecosystem
            </span>
          </div>

          <h2
            className="mb-5 text-3xl font-bold md:text-4xl lg:text-5xl"
            style={{ fontFamily: "Playfair Display, serif", color: "#2D2A26" }}
          >
            AI-Powered{" "}
            <span
              style={{
                background:
                  "linear-gradient(135deg, #C6933A 0%, #E3C075 50%, #C6933A 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Research Tools
            </span>
          </h2>

          <p
            className="text-base leading-relaxed lg:text-lg"
            style={{ fontFamily: "Inter, sans-serif", color: "#6B6764" }}
          >
            An integrated ecosystem designed for scholars, researchers, and
            seekers to explore centuries of Jain knowledge with modern
            AI-powered assistance.
          </p>
        </div>

        {/* Tools Grid - 4 columns on large screens */}
        <div className="mb-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {researchTools.map((tool, index) => (
            <Link
              key={tool.title}
              to={tool.href}
              className="group relative rounded-2xl border border-[#E8E4DD] bg-white p-6 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl"
              style={{
                boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
              }}
            >
              {/* Hover gradient overlay */}
              <div
                className="absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(201,162,39,0.05) 0%, transparent 100%)",
                }}
              />

              {/* Icon */}
              <div
                className="relative mb-5 flex h-14 w-14 items-center justify-center rounded-xl shadow-lg transition-all duration-300 group-hover:scale-110"
                style={{ background: tool.iconBg }}
              >
                <tool.icon className="h-7 w-7 text-white" />
              </div>

              {/* Content */}
              <div className="relative">
                <h3
                  className="mb-1 text-xl font-bold transition-colors duration-300 group-hover:text-[#C9A227]"
                  style={{
                    fontFamily: "Playfair Display, serif",
                    color: "#2D2A26",
                  }}
                >
                  {tool.title}
                </h3>
                <p
                  className="mb-3 text-sm font-semibold"
                  style={{ fontFamily: "Inter, sans-serif", color: "#C9A227" }}
                >
                  {tool.subtitle}
                </p>

                <p
                  className="mb-5 text-sm leading-relaxed"
                  style={{ fontFamily: "Inter, sans-serif", color: "#6B6764" }}
                >
                  {tool.description}
                </p>

                <div
                  className="flex items-center gap-1.5 text-sm font-semibold transition-all duration-300 group-hover:gap-3"
                  style={{ fontFamily: "Inter, sans-serif", color: "#C9A227" }}
                >
                  <span>Learn More</span>
                  <ArrowRight className="h-4 w-4" />
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center">
          <Button
            size="lg"
            className="rounded-xl px-8 py-6 text-base font-medium text-white shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl"
            style={{
              fontFamily: "Inter, sans-serif",
              background: "linear-gradient(135deg, #C9A227 0%, #D4A03C 100%)",
            }}
            asChild
          >
            <Link to="/research">
              Explore Research Hub
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
